import React, { useState } from "react";
import { Input } from "../ui/input";
import { Plus, Trash2, ChevronDown, ChevronRight, Check, Users } from "lucide-react";
import RawNumInput from "./RawNumInput";
import { UNIT_LABEL } from "../../lib/calculator";
import { autoSelectOnFocus } from "../../lib/autoNameSelect";
import { EditableDescription } from "./EditableDescription";

// v6 F42k — Fase 3 Zone-vereenvoudiging: Zelfstandige Buitenelementen-zone.
// Elementen worden afzonderlijk opgemeten (naam + dimensies), maar automatisch
// gegroepeerd op (kind + material). Elke groep krijgt ÉÉN gedeelde
// werkpostensjabloon. Werk-toggle in een groep synct measurements over alle
// elementen in die groep (elk met eigen extra_element_id → per-element qty
// blijft correct in backend calc). Prijs-override op groepregel gaat naar
// alle measurements in de groep.
//
// Deze component HERGEBRUIKT WORK_ITEMS / resolveWorkItems / isItemAllowed
// uit FacadeExtraElementBlock om DRY-code te houden. Voor auto-grouping is
// er een aparte rendering-laag boven de bestaande data.

import {
  ELEMENT_KINDS,
  MATERIALS,
  KIND_LABEL,
  CHOICE_GROUP_LABELS,
  resolveWorkItems,
  computeElementQty,
  fmtEUR,
} from "./FacadeExtraElementBlock";

// Groepeer elementen op (kind + material). "" (leeg materiaal) telt als eigen groep.
function groupElements(elements) {
  const groups = new Map();
  for (const el of (elements || [])) {
    const key = `${el.kind || "other"}::${el.material || ""}`;
    if (!groups.has(key)) groups.set(key, { kind: el.kind || "other", material: el.material || "", elements: [] });
    groups.get(key).elements.push(el);
  }
  return Array.from(groups.values());
}

// Compact meting-input voor één element binnen een groep. Toont enkel
// naam + dimensies (geen materiaal/kind — die komen van de groep).
function ElementMeasureRow({ elem, onUpdate, onRemove, zoneId, groupKey }) {
  const unit = elem.unit || "lm";
  const unitLabel = UNIT_LABEL[unit] || unit;
  const q = computeElementQty(elem);
  return (
    <div
      className="flex items-center gap-2 py-1.5 px-2 rounded-md bg-white/70 border border-[#EDECE6]"
      data-testid={`elem-row-${zoneId}-${elem.id}`}
    >
      <Input
        value={elem.name || ""}
        onChange={(e) => onUpdate({ name: e.target.value })}
        onFocus={autoSelectOnFocus}
        placeholder="Naam / locatie"
        className="h-8 flex-1 min-w-0 rounded-md text-sm"
        data-testid={`elem-name-${zoneId}-${elem.id}`}
      />
      {unit === "lm" && (
        <RawNumInput
          value={elem.dim_length ?? 0}
          onChange={(v) => onUpdate({ dim_length: typeof v === "number" ? v : 0 })}
          className="h-8 w-20 rounded-md text-right font-mono text-sm"
          data-testid={`elem-len-${zoneId}-${elem.id}`}
        />
      )}
      {unit === "m2" && (
        <>
          <RawNumInput
            value={elem.dim_length ?? 0}
            onChange={(v) => onUpdate({ dim_length: typeof v === "number" ? v : 0 })}
            className="h-8 w-16 rounded-md text-right font-mono text-sm"
            data-testid={`elem-len-${zoneId}-${elem.id}`}
          />
          <span className="text-xs text-[#5D7259]">×</span>
          <RawNumInput
            value={elem.dim_width ?? 0}
            onChange={(v) => onUpdate({ dim_width: typeof v === "number" ? v : 0 })}
            className="h-8 w-16 rounded-md text-right font-mono text-sm"
            data-testid={`elem-wid-${zoneId}-${elem.id}`}
          />
        </>
      )}
      {unit === "stuks" && (
        <RawNumInput
          value={elem.qty ?? 0}
          onChange={(v) => onUpdate({ qty: typeof v === "number" ? v : 0 })}
          className="h-8 w-16 rounded-md text-right font-mono text-sm"
          data-testid={`elem-qty-${zoneId}-${elem.id}`}
        />
      )}
      <span
        className="text-sm font-mono text-[#2C2C2A] w-24 text-right"
        data-testid={`elem-total-qty-${zoneId}-${elem.id}`}
      >
        = {q.toLocaleString("nl-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {unitLabel}
      </span>
      <button
        type="button"
        onClick={onRemove}
        className="w-7 h-7 rounded-md hover:bg-red-50 flex items-center justify-center text-[#D9534F]"
        data-testid={`elem-remove-${zoneId}-${elem.id}`}
        aria-label="Verwijderen"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// Werkpost-regel voor een GROEP: één checkbox schakelt de PC in/uit voor
// ALLE elementen in de groep. Prijs-override synct ook naar alle
// measurements van deze groep.
function GroupWorkLine({ item, groupMeas, groupElements: gElements, unitLabel, groupTotalQty, zoneId, groupKey, onToggleForGroup, onUpdatePriceForGroup, onUpdateDescForGroup }) {
  const anyChecked = gElements.some((el) => groupMeas.byElemThenPc[el.id]?.[item.pc.id]);
  const allChecked = gElements.every((el) => groupMeas.byElemThenPc[el.id]?.[item.pc.id]);
  const partiallyChecked = anyChecked && !allChecked;
  const defaultPrice = +item.pc.default_price;
  // Bepaal de effectieve override: neem de eerste niet-null override
  const firstOverride = (() => {
    for (const el of gElements) {
      const m = groupMeas.byElemThenPc[el.id]?.[item.pc.id];
      if (m && m.unit_price_override != null && m.unit_price_override !== "") return +m.unit_price_override;
    }
    return null;
  })();
  // v6 F42k v14b (final polish) — Eerste description_override in de groep
  // (spiegel van firstOverride voor prijs); zelfde override wordt toegepast
  // op alle measurements in deze groep.
  const firstDescOverride = (() => {
    for (const el of gElements) {
      const m = groupMeas.byElemThenPc[el.id]?.[item.pc.id];
      if (m && m.description_override != null && m.description_override !== "") return String(m.description_override);
    }
    return null;
  })();
  // Snapshot uit measurement (per-quote historische omschrijving) of item.display als fallback.
  const firstDescSnapshot = (() => {
    for (const el of gElements) {
      const m = groupMeas.byElemThenPc[el.id]?.[item.pc.id];
      if (m && m.description_snapshot) return String(m.description_snapshot);
    }
    return null;
  })();
  const effDescDefault = firstDescSnapshot || item.display;
  const unitPrice = firstOverride ?? defaultPrice;
  const lineTotal = anyChecked ? groupTotalQty * unitPrice : 0;
  const [priceOpen, setPriceOpen] = useState(false);

  const priceInput = (
    <>
      <span className="text-[11px] text-[#4F4F4D]">€</span>
      <RawNumInput
        value={firstOverride != null ? firstOverride : ""}
        allowEmpty
        onChange={(v) => onUpdatePriceForGroup(item.pc.id, v)}
        placeholder={defaultPrice.toFixed(2)}
        className="w-20 h-8 rounded-md text-right text-sm"
        data-testid={`group-work-price-${zoneId}-${groupKey}-${item.pc.code}`}
      />
      {firstOverride != null && (
        <span
          className="text-[9px] uppercase tracking-wider font-bold text-[#B07A4F]"
          title="Aangepast — voor alle elementen in deze groep"
        >
          Aangepast
        </span>
      )}
    </>
  );

  return (
    <div
      className={`flex items-start sm:items-center gap-3 px-3 py-2 ${anyChecked ? "bg-[#FFF8F2]" : "hover:bg-[#FAF9F6]"}`}
      data-testid={`group-work-row-${zoneId}-${groupKey}-${item.pc.code}`}
    >
      <button
        type="button"
        onClick={() => onToggleForGroup(item)}
        className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition mt-0.5 sm:mt-0 ${
          allChecked ? "text-white border-[#6B8E6B] bg-[#6B8E6B]"
          : partiallyChecked ? "border-[#6B8E6B] bg-[#FFF8F2]"
          : "border-[#C9C8C2] hover:border-[#6B8E6B]"
        }`}
        data-testid={`group-work-toggle-${zoneId}-${groupKey}-${item.pc.code}`}
        aria-label={anyChecked ? "Uitschakelen voor groep" : "Inschakelen voor groep"}
      >
        {allChecked && <Check className="w-3.5 h-3.5" />}
        {partiallyChecked && <div className="w-2.5 h-0.5 bg-[#6B8E6B]" />}
      </button>
      <div className="flex-1 min-w-0">
        {anyChecked ? (
          <div className="text-sm font-medium text-[#2C2C2A]">
            <EditableDescription
              value={firstDescOverride || effDescDefault}
              override={firstDescOverride}
              defaultValue={effDescDefault}
              onOverride={(text) => onUpdateDescForGroup(item.pc.id, text)}
              onReset={() => onUpdateDescForGroup(item.pc.id, null)}
              testId={`group-work-desc-${zoneId}-${groupKey}-${item.pc.code}`}
            />
          </div>
        ) : (
          <div className="text-sm font-medium text-[#2C2C2A] line-clamp-2 sm:truncate">{item.display}</div>
        )}
        <div className="text-[10px] text-[#5D7259] font-mono truncate">{item.pc.code} · {item.pc.work_category}</div>
        <div
          className="sm:hidden text-[11px] font-mono text-[#2C2C2A] mt-1"
          data-testid={`group-work-summary-mobile-${zoneId}-${groupKey}-${item.pc.code}`}
        >
          {groupTotalQty.toLocaleString("nl-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {unitLabel} × {fmtEUR(unitPrice)}/{unitLabel}
        </div>
        {anyChecked && (
          <div className="sm:hidden mt-1.5">
            {!priceOpen ? (
              <button
                type="button"
                onClick={() => setPriceOpen(true)}
                className="text-[11px] font-medium text-[#7F4124] underline decoration-dotted"
                data-testid={`group-work-price-edit-mobile-${zoneId}-${groupKey}-${item.pc.code}`}
              >
                {firstOverride != null ? "Prijs wijzigen (groep)" : "Prijs aanpassen (groep)"}
              </button>
            ) : (
              <div className="flex items-center gap-2 flex-wrap">
                {priceInput}
                <button type="button" onClick={() => setPriceOpen(false)} className="text-[11px] text-[#5D7259] underline">Klaar</button>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="text-[11px] text-[#4F4F4D] hidden sm:block w-12 text-right">{unitLabel}</div>
      <div className="hidden sm:flex flex-wrap items-center gap-x-1 gap-y-0.5 w-24 justify-end">
        {anyChecked ? priceInput : (<span className="text-[11px] text-[#4F4F4D]">€ {unitPrice.toFixed(2)}/{unitLabel}</span>)}
      </div>
      <div className="w-20 text-right flex-shrink-0">
        <div className="text-sm font-bold text-[#2C2C2A]">{groupTotalQty.toLocaleString("nl-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {unitLabel}</div>
        <div className={`sm:hidden text-sm font-bold mt-0.5 ${anyChecked ? "text-[#6B8E6B]" : "text-[#A8A7A0]"}`}>{fmtEUR(anyChecked ? lineTotal : 0)}</div>
      </div>
      <div className="w-24 text-right hidden sm:block">
        <div className={`text-sm font-bold ${anyChecked ? "text-[#6B8E6B]" : "text-[#A8A7A0]"}`}>{fmtEUR(lineTotal)}</div>
      </div>
    </div>
  );
}

// Eén groep-card
function GroupCard({ group, priceCodes, measurements, zoneId, onUpdateElement, onRemoveElement, onAddElementToGroup, onSetMaterialForGroup, onAddMeasurement, onUpdateMeasurement, onRemoveMeasurement }) {
  const [collapsed, setCollapsed] = useState(false);
  const kindLabel = KIND_LABEL[group.kind] || group.kind;
  const matLabel = MATERIALS.find((m) => m.value === group.material)?.label || "— Materiaal —";
  const groupKey = `${group.kind}-${group.material || "none"}`;

  // Alle elementen in de groep delen dezelfde unit (kind bepaalt allowed_units;
  // in nieuwe zones is dat er meestal maar 1). We pakken de unit van het
  // eerste element als representatief.
  const unit = group.elements[0]?.unit || "lm";
  const unitLabel = UNIT_LABEL[unit] || unit;

  // Totale groep-hoeveelheid (som van per-element computed qty)
  const groupTotalQty = group.elements.reduce((s, el) => s + computeElementQty(el), 0);

  // resolveWorkItems verwacht een enkel elem; we passeren het eerste (voor
  // forfait-naming; niet cruciaal in group-context want forfait typisch niet gegroepeerd)
  const worksForGroup = resolveWorkItems(group.kind, unit, group.material, priceCodes, group.elements[0]);

  // measByPc gecombineerd: per element bewaren we welke PC-ids reeds gekoppeld zijn
  const measByElemThenPc = {};
  for (const el of group.elements) measByElemThenPc[el.id] = {};
  for (const m of measurements) {
    if (m.extra_element_id && measByElemThenPc[m.extra_element_id]) {
      measByElemThenPc[m.extra_element_id][m.price_code_id] = m;
    }
  }
  const groupMeas = { byElemThenPc: measByElemThenPc };

  // v6 F42k Correctieronde v7 — Override-preservation bij choice-group wissel.
  // Bij uitschakelen van een PC (via toggle-off OF choice-group wissel):
  // snapshot de huidige unit_price_override naar élk element.saved_price_overrides.
  // Bij inschakelen: lees uit el.saved_price_overrides om override te herstellen.
  // Scope = per element (FacadeExtraElement.saved_price_overrides field bestaat
  // al in backend). Kruisbesmetting tussen elementen/groepen is uitgesloten
  // omdat elk element zijn eigen saved-overrides map heeft.
  const _snapshotOverrideForPc = (pcId) => {
    for (const el of group.elements) {
      const m = measByElemThenPc[el.id]?.[pcId];
      if (m && m.unit_price_override != null && m.unit_price_override !== "") {
        const current = { ...(el.saved_price_overrides || {}) };
        current[pcId] = +m.unit_price_override;
        onUpdateElement(el.id, { saved_price_overrides: current });
      }
    }
  };
  const _restoreOverrideForPc = (pcId) => {
    // Retourneert eerste beschikbare saved override in de groep (elementen
    // in dezelfde groep delen dezelfde prijs — group-price sync via updatePriceForGroup).
    for (const el of group.elements) {
      const v = el.saved_price_overrides?.[pcId];
      if (v != null) return +v;
    }
    return null;
  };

  // Choice-group: bij toggle van PC met choice_group → eerst alle conflicting
  // measurements weghalen bij alle elementen, dan toevoegen.
  const toggleForGroup = (item) => {
    const { pc, override, choice_group } = item;
    const anyChecked = group.elements.some((el) => measByElemThenPc[el.id]?.[pc.id]);
    if (anyChecked) {
      // v6 F42k Correctieronde v7 — Snapshot huidige override voor eventuele
      // terugkeer via re-toggle later.
      _snapshotOverrideForPc(pc.id);
      // Verwijder de PC bij alle elementen die het hebben
      for (const el of group.elements) {
        const m = measByElemThenPc[el.id]?.[pc.id];
        if (m) onRemoveMeasurement(m.id);
      }
      return;
    }
    // Choice-group: eerst conflicting posts weghalen (met snapshot van hun overrides)
    if (choice_group) {
      const conflicting = worksForGroup.filter((w) => w.choice_group === choice_group && w.pc.id !== pc.id);
      for (const w of conflicting) {
        _snapshotOverrideForPc(w.pc.id);
        for (const el of group.elements) {
          const m = measByElemThenPc[el.id]?.[w.pc.id];
          if (m) onRemoveMeasurement(m.id);
        }
      }
    }
    // Herstel eventuele eerdere override voor deze PC uit saved_price_overrides
    const restoredOverride = _restoreOverrideForPc(pc.id);
    // Voeg PC toe aan ALLE elementen in de groep
    for (const el of group.elements) {
      const desc = item.override ? item.override(el.kind, el) : pc.description;
      onAddMeasurement({
        id: crypto.randomUUID(),
        zone_id: zoneId,
        room_id: null,
        description: desc,
        formula: "extra_element_qty",
        extra_element_id: el.id,
        length: 0, width: 0, height: 0, quantity: 0,
        multiplier: 1, coats: 2,
        price_code_id: pc.id,
        action_code: pc.code,
        unit_price_override: restoredOverride,
      });
    }
  };

  const updatePriceForGroup = (pcId, newValue) => {
    const override = (newValue === "" || newValue === null || newValue === undefined) ? null : +newValue;
    for (const el of group.elements) {
      const m = measByElemThenPc[el.id]?.[pcId];
      if (m) onUpdateMeasurement(m.id, { unit_price_override: override });
    }
  };

  // v6 F42k v14b (final polish) — Description-override voor alle measurements
  // in de groep (dezelfde tekst; sync-pattern spiegelt updatePriceForGroup).
  const updateDescForGroup = (pcId, newText) => {
    const override = (newText === "" || newText === null || newText === undefined) ? null : String(newText);
    for (const el of group.elements) {
      const m = measByElemThenPc[el.id]?.[pcId];
      if (m) onUpdateMeasurement(m.id, { description_override: override });
    }
  };

  const setMaterial = (newMat) => {
    // Update material op alle elementen in deze groep (regroups automatisch)
    for (const el of group.elements) {
      onUpdateElement(el.id, { material: newMat });
    }
    // Purge measurements: bij material-wissel worden de bestaande measurements
    // opnieuw geëvalueerd. We verwijderen simpelweg alle bestaande measurements
    // van deze elementen — user selecteert opnieuw uit nieuwe filter.
    for (const el of group.elements) {
      for (const m of Object.values(measByElemThenPc[el.id] || {})) {
        if (m) onRemoveMeasurement(m.id);
      }
    }
  };

  const totalForGroup = worksForGroup.reduce((acc, item) => {
    const anyChecked = group.elements.some((el) => measByElemThenPc[el.id]?.[item.pc.id]);
    if (!anyChecked) return acc;
    const firstOverride = (() => {
      for (const el of group.elements) {
        const m = measByElemThenPc[el.id]?.[item.pc.id];
        if (m && m.unit_price_override != null && m.unit_price_override !== "") return +m.unit_price_override;
      }
      return null;
    })();
    const price = firstOverride ?? +item.pc.default_price;
    return acc + price * groupTotalQty;
  }, 0);

  return (
    <div
      className="rounded-xl bg-white border border-[#E5E2DC] overflow-hidden"
      data-testid={`group-card-${zoneId}-${groupKey}`}
    >
      {/* Group header */}
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center gap-2 px-3 py-2.5 bg-[#F2F5EE] hover:bg-[#EAF0E4] text-left"
        data-testid={`group-toggle-${zoneId}-${groupKey}`}
      >
        {collapsed ? <ChevronRight className="w-4 h-4 text-[#3E5A3E]" /> : <ChevronDown className="w-4 h-4 text-[#3E5A3E]" />}
        <Users className="w-4 h-4 text-[#3E5A3E]" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-[#2C2C2A]">
            {kindLabel} × {group.elements.length}
            <span className="text-[#5D7259] font-normal"> · {matLabel}</span>
          </div>
          <div className="text-[10px] text-[#5D7259] uppercase tracking-wider">
            Totaal: {groupTotalQty.toLocaleString("nl-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {unitLabel}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-[#5D7259]">Groep-totaal</div>
          <div className="font-mono font-bold text-[#6B8E6B] text-sm" data-testid={`group-total-${zoneId}-${groupKey}`}>
            {fmtEUR(totalForGroup)}
          </div>
        </div>
      </button>

      {!collapsed && (
        <div className="px-3 pb-3 pt-2 space-y-3">
          {/* Group-level material selector */}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[#5D7259] block mb-1">Materiaal (voor hele groep)</label>
            <select
              value={group.material}
              onChange={(e) => setMaterial(e.target.value)}
              className="h-9 w-full rounded-lg border border-[#E5E2DC] px-2 text-sm bg-white"
              data-testid={`group-material-${zoneId}-${groupKey}`}
            >
              {MATERIALS.map((m) => (<option key={m.value || "none"} value={m.value}>{m.label}</option>))}
            </select>
          </div>

          {/* Individual elements */}
          <div className="space-y-1.5" data-testid={`group-elements-${zoneId}-${groupKey}`}>
            <div className="text-[10px] uppercase tracking-wider font-bold text-[#5D7259]">Elementen ({group.elements.length})</div>
            {group.elements.map((el) => (
              <ElementMeasureRow
                key={el.id}
                elem={el}
                onUpdate={(patch) => onUpdateElement(el.id, patch)}
                onRemove={() => onRemoveElement(el.id)}
                zoneId={zoneId}
                groupKey={groupKey}
              />
            ))}
            <button
              type="button"
              onClick={() => onAddElementToGroup(group.kind, group.material)}
              className="text-xs px-3 h-8 rounded-md border border-dashed border-[#6B8E6B]/50 hover:border-[#6B8E6B] hover:bg-[#F2F5EE] text-[#3E5A3E] font-medium gap-1 inline-flex items-center"
              data-testid={`group-add-elem-${zoneId}-${groupKey}`}
            >
              <Plus className="w-3 h-3" /> Element toevoegen aan deze groep
            </button>
          </div>

          {/* Shared works list */}
          <div className="border border-[#E5E2DC] rounded-lg overflow-hidden divide-y divide-[#F0EFEA] bg-white">
            {!group.material ? (
              <div className="p-3 text-[12px] text-[#5D7259] italic">Kies eerst het materiaal om de geschikte behandelingen te tonen.</div>
            ) : worksForGroup.length === 0 ? (
              <div className="p-3 text-[11px] text-[#5D7259] italic">
                Geen werkposten beschikbaar voor deze combinatie ({kindLabel} · {unitLabel} · {matLabel}).
              </div>
            ) : (
              worksForGroup.map((item, i) => {
                const prev = worksForGroup[i - 1];
                const isFirstInGroup = item.choice_group && (!prev || prev.choice_group !== item.choice_group);
                return (
                  <React.Fragment key={item.pc.id}>
                    {isFirstInGroup && (
                      <div className="px-3 py-1 bg-[#F2F5EE] text-[9px] uppercase tracking-[0.15em] font-bold text-[#3E5A3E]">
                        <span>{CHOICE_GROUP_LABELS[item.choice_group] || "Kies één"}</span>
                      </div>
                    )}
                    <GroupWorkLine
                      item={item}
                      groupMeas={groupMeas}
                      groupElements={group.elements}
                      unitLabel={unitLabel}
                      groupTotalQty={groupTotalQty}
                      zoneId={zoneId}
                      groupKey={groupKey}
                      onToggleForGroup={toggleForGroup}
                      onUpdatePriceForGroup={updatePriceForGroup}
                      onUpdateDescForGroup={updateDescForGroup}
                    />
                  </React.Fragment>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ExtraElementsGroupedBlock({ zone, priceCodes, measurements, onUpdateZone, onAddMeasurement, onUpdateMeasurement, onRemoveMeasurement }) {
  const elements = zone.facade_extra_elements || [];
  const groups = groupElements(elements);

  const updateElement = (elemId, patch) => {
    const next = elements.map((el) => (el.id === elemId ? { ...el, ...patch } : el));
    onUpdateZone({ facade_extra_elements: next });
  };
  const removeElement = (elemId) => {
    // Verwijder gekoppelde measurements
    const linked = measurements.filter((m) => m.extra_element_id === elemId);
    for (const m of linked) onRemoveMeasurement(m.id);
    const next = elements.filter((el) => el.id !== elemId);
    onUpdateZone({ facade_extra_elements: next });
  };
  const addElement = (kind, material) => {
    const kindDef = ELEMENT_KINDS.find((k) => k.value === kind) || ELEMENT_KINDS[0];
    // v6 F42k Correctieronde v2 — auto-naam per kind (bv. "Dakoversteek 1")
    // zodat de select-on-focus UX werkt. Tel bestaande elementen van hetzelfde
    // kind om oplopende suffixen ("Dakoversteek 2") te geven.
    const sameKindCount = elements.filter((el) => el.kind === kindDef.value).length;
    const autoName = kindDef.value === "other" ? "" : `${kindDef.label} ${sameKindCount + 1}`;
    const next = [...elements, {
      id: crypto.randomUUID(),
      name: autoName,
      kind: kindDef.value,
      unit: kindDef.default_unit,
      qty: 0, dim_length: 0, dim_width: 0, manual_area: 0,
      calc_mode: "lxw", material: material || "", material_note: "", notes: "",
    }];
    onUpdateZone({ facade_extra_elements: next });
  };

  return (
    <div
      className="mb-3 p-3 rounded-xl bg-[#FAFAF7] border border-[#E5E2DC] space-y-3"
      data-testid={`extra-elements-grouped-${zone.id}`}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs uppercase tracking-[0.15em] font-bold text-[#6B8E6B]">
          Buitenelementen — automatisch gegroepeerd ({elements.length} elementen · {groups.length} groepen)
        </div>
        <div className="text-[10px] text-[#5D7259] italic">
          Gegroepeerd op type + materiaal · gedeelde werkposten per groep
        </div>
      </div>

      {groups.length > 0 && (
        <div className="space-y-2" data-testid={`groups-list-${zone.id}`}>
          {groups.map((g) => (
            <GroupCard
              key={`${g.kind}::${g.material}`}
              group={g}
              priceCodes={priceCodes}
              measurements={measurements}
              zoneId={zone.id}
              onUpdateElement={updateElement}
              onRemoveElement={removeElement}
              onAddElementToGroup={addElement}
              onAddMeasurement={onAddMeasurement}
              onUpdateMeasurement={onUpdateMeasurement}
              onRemoveMeasurement={onRemoveMeasurement}
            />
          ))}
        </div>
      )}

      {/* Nieuw type toevoegen */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid={`new-kind-buttons-${zone.id}`}>
        {ELEMENT_KINDS.filter((k) => k.value !== "other").map((k) => (
          <button
            key={k.value}
            type="button"
            onClick={() => addElement(k.value, "")}
            className="text-sm px-3 h-10 rounded-lg border-2 border-dashed border-[#6B8E6B]/50 hover:border-[#6B8E6B] hover:bg-white text-[#3E5A3E] font-medium gap-1 inline-flex items-center justify-center"
            data-testid={`add-kind-${k.value}-${zone.id}`}
          >
            <Plus className="w-3.5 h-3.5" /> {k.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => addElement("other", "")}
          className="text-sm px-3 h-10 rounded-lg border-2 border-dashed border-[#6B8E6B]/50 hover:border-[#6B8E6B] hover:bg-white text-[#3E5A3E] font-medium gap-1 inline-flex items-center justify-center"
          data-testid={`add-kind-other-${zone.id}`}
        >
          <Plus className="w-3.5 h-3.5" /> Ander
        </button>
      </div>
    </div>
  );
}
